import { ref } from "vue";
import { listTranslationsApi } from "@/api/svc005Api";

/**
 * 翻译表内存缓存（模块级 ref，全局共享）。
 *
 * <p>App 初始化时调 loadTranslations 加载，页面展示设备名时调 translateName 转中文。
 * 刷新浏览器/关闭重开都会重新加载（内存不持久）。
 * 翻译表变化时需手动调 loadTranslations 刷新（后续添加触发点）。</p>
 */

/** en_name -> dev_name 映射（展示：英文转中文） */
const translationMap = ref<Map<string, string>>(new Map());

/** dev_name -> en_name 反查映射（保存：中文转英文） */
let reverseMap = new Map<string, string>();

/** 是否已加载过 */
let loaded = false;

/** 进行中的加载请求（并发调用共享同一次请求，避免重复拉取） */
let loadPromise: Promise<void> | null = null;

/**
 * 加载翻译表到内存缓存。
 *
 * <p>过滤自我映射记录（enName === devName，纯英文设备名入库产生）：
 * customer 翻译流程会主动创建此类记录，若同一 enName 同时存在真翻译与自我映射，
 * Map 按遍历顺序后写覆盖会让"英文转中文"退化为原值（与后端 /translation/all 的防御一致）。</p>
 *
 * @param force true=强制重新拉取（保存成功后后端可能新入库了翻译，默认 false 复用进行中的请求）
 */
export const loadTranslations = async (force = false) => {
  // 成功加载过且非强制刷新：幂等短路，避免每次 loadData 重复拉全表
  if (loaded && !force) {
    return;
  }
  if (loadPromise && !force) {
    // 已有进行中的请求：共享结果（App 启动与页面 onMounted 并发场景；
    // 注意共享的请求若因未登录 401 失败，loaded 仍为 false，调用方可再次调用重试）
    return loadPromise;
  }
  loadPromise = (async () => {
    const result = await listTranslationsApi();
    if (result) {
      const map = new Map<string, string>();
      const rev = new Map<string, string>();
      result.forEach((t) => {
        if (t.enName && t.devName && t.enName !== t.devName) {
          map.set(t.enName, t.devName);
          // 反向映射首个胜出（同一中文多条英文记录时保持稳定，与后端 LIMIT 1 语义一致）
          if (!rev.has(t.devName)) {
            rev.set(t.devName, t.enName);
          }
        }
      });
      translationMap.value = map;
      reverseMap = rev;
      loaded = true;
    }
  })();
  try {
    await loadPromise;
  } finally {
    loadPromise = null;
  }
};

/**
 * 英文设备名转中文（翻译表命中返回中文，未命中返回原英文）。
 *
 * @param enName 英文设备名
 * @returns 中文设备名（未命中返回原英文）
 */
export const translateName = (enName: string | null | undefined): string => {
  if (!enName) return "";
  return translationMap.value.get(enName) || enName;
};

/**
 * 翻译表是否已加载
 */
export const isTranslationLoaded = (): boolean => loaded;

/**
 * 数据来源 src 中的英文设备名转中文（页面展示用）。
 *
 * <p>库内 data_source 存英文（如 batArray?:x3），页面展示/编辑统一为中文（如 电池域?:x3）。
 * 设备名为 ? 前的部分，翻译表未命中时原样返回。</p>
 *
 * @param src 数据来源（英文态，如 "batArray?:x3"）
 * @returns 中文态数据来源（如 "电池域?:x3"）
 */
export const translateSrcToZh = (src: string | null | undefined): string => {
  if (!src) return "";
  const idx = src.indexOf("?");
  const dev = idx > 0 ? src.slice(0, idx) : src;
  const rest = idx > 0 ? src.slice(idx) : "";
  const zh = translationMap.value.get(dev);
  return zh ? zh + rest : src;
};

/**
 * 运营管理协议电池类别专用映射（中→英）：终端以 batArray 前缀识别电池设备，
 * 各类电池叫法（电池域/电池组/电池堆/电池系统）统一映射为 batArray。
 * 与后端 ShipProtocolTemplateServiceImpl.BATTERY_CATEGORY_EN 同步维护。
 */
const BATTERY_CATEGORY_EN: Record<string, string> = {
  电池域: "batArray",
  电池组: "batArray",
  电池堆: "batArray",
  电池系统: "batArray",
};

/**
 * 数据来源 src 中的中文设备名转英文（保存提交用）。
 *
 * <p>页面输入的中文设备名提交前转英文，保证库内 data_source 始终为英文；
 * 电池类别词优先走专用映射（统一 batArray），其余查翻译表反向映射，
 * 未命中时原样提交，由后端保存入口 normalizeSrc 兜底翻译（字典+拼音并入库）。</p>
 *
 * @param src 数据来源（中文态，如 "电池域?:x3" / "电池系统?:x3"）
 * @returns 英文态数据来源（如 "batArray?:x3"，未命中原样返回）
 */
export const translateSrcToEn = (src: string | null | undefined): string => {
  if (!src) return "";
  const idx = src.indexOf("?");
  const dev = idx > 0 ? src.slice(0, idx) : src;
  const rest = idx > 0 ? src.slice(idx) : "";
  // 电池类别专用映射优先（电池域/电池组/电池堆/电池系统 -> batArray）
  const en = BATTERY_CATEGORY_EN[dev] || reverseMap.get(dev);
  return en ? en + rest : src;
};
